/**
 * DPoP proof builder (RFC 9449) for the browser. The proof assembly itself —
 * the JWS, the RFC 7638 thumbprint, base64url — lives in core
 * (`services/auth/dpopProof`), which mobile shares; everything below is the
 * WebCrypto half: the key pair, and the digest / signature / random primitives
 * core's builder takes as parameters.
 *
 * `ES256` is the algorithm here because P-256 ECDSA is the one alg the
 * provider advertises that every supported browser's WebCrypto implements, and
 * WebCrypto's ECDSA signatures are already the raw `r||s` form a JWS wants —
 * no DER conversion anywhere below.
 *
 * Every function takes `subtle` (`crypto.subtle`) as its first argument, so
 * the same code runs on the page and under jest's jsdom environment (where
 * `crypto.subtle` is missing and the tests inject node's).
 */

// The core leaf, not the `@formue-app/core` barrel: this module is loaded by
// the API layer on every request and has no business pulling core's whole
// circular import chain in behind it.
import {
  computeThumbprint as computeJwkThumbprint,
  createDpopProof as createProof,
  utf8Bytes,
} from '@formue-app/core/src/services/auth/dpopProof';

export {
  base64url,
  stripUrlForHtu,
} from '@formue-app/core/src/services/auth/dpopProof';

const subtleSha256 = async (subtle, input) =>
  new Uint8Array(await subtle.digest('SHA-256', utf8Bytes(input)));

/**
 * Generate the session's key pair: ECDSA P-256, **non-extractable**, so page
 * script can sign with it but never read it (decision 3 of the DPoP plan).
 */
export const generateDpopKeyPair = async (subtle) => {
  const { privateKey, publicKey } = await subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['sign', 'verify']
  );
  return { privateKey, publicKey };
};

/**
 * The public half of the key as the JWK that goes into the proof header —
 * only the four members RFC 7638 thumbprints over, so a private component or
 * WebCrypto's `ext` / `key_ops` bookkeeping can never leak into a proof.
 */
export const exportPublicJwk = async (subtle, publicKey) => {
  const { kty, crv, x, y } = await subtle.exportKey('jwk', publicKey);
  return { kty, crv, x, y };
};

/** RFC 7638 JWK thumbprint of the public key, digested with WebCrypto. */
export const computeThumbprint = async (subtle, publicJwk) =>
  computeJwkThumbprint({
    sha256: (input) => subtleSha256(subtle, input),
    publicJwk,
  });

const defaultGetRandomValues = (bytes) => window.crypto.getRandomValues(bytes);

/**
 * Build one proof for one request (RFC 9449 §4.2). `htu` is stripped of query
 * and fragment, `ath` is added only on resource requests (where an access
 * token is being presented) and `nonce` only when a server has asked for one
 * — the token endpoint does not, but the parameter is here so nonces can be
 * wired without touching the builder.
 *
 * Every call mints a fresh `jti`, so a retried request never replays one.
 * `getRandomValues` is injectable because jest's jsdom global has no
 * `crypto.getRandomValues`.
 */
export const createDpopProof = async (
  subtle,
  {
    privateKey,
    publicJwk,
    htm,
    htu,
    accessToken,
    nonce,
    getRandomValues = defaultGetRandomValues,
  }
) =>
  createProof({
    publicJwk,
    htm,
    htu,
    accessToken,
    nonce,
    sha256: (input) => subtleSha256(subtle, input),
    sign: (bytes) =>
      subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, privateKey, bytes),
    randomBytes: (count) => getRandomValues(new Uint8Array(count)),
  });
